function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length > 11) return '';

  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  } else {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
}

/**
 * Formats a Brazilian phone number for WhatsApp API
 * Ensures the number has the +55 country code and proper length
 * @param value - The phone number string to format
 * @returns Formatted phone number with +55 prefix, or empty string if invalid
 */
function formatPhoneNumberForWhatsApp(value: string): string {
  // Input validation
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Already has +55 prefix
  if (value.startsWith('+55')) {
    return value;
  }

  // Has 55 prefix but missing +
  if (value.startsWith('55')) {
    return `+${value}`;
  }

  // No country code, add +55
  return `+55${value}`;
}

/**
 * Validates if a digit string is a valid Brazilian phone number
 * @param digits - String containing only digits
 * @returns true if valid Brazilian phone number format
 */
function isValidBrazilianPhoneNumber(digits: string): boolean {
  // Brazilian phone numbers: 10 digits (landline) or 11 digits (mobile)
  if (digits.length !== 10 && digits.length !== 11) {
    return false;
  }

  // Area code should be between 11-99
  const areaCode = Number.parseInt(digits.slice(0, 2), 10);
  if (areaCode < 11 || areaCode > 99) {
    return false;
  }

  // For 11-digit numbers (mobile), 3rd digit should be 9
  if (digits.length === 11 && digits[2] !== '9') {
    return false;
  }

  return true;
}

/**
 * Sanitizes a Brazilian phone number string into the canonical format 55XXXXXXXXXXX.
 * Handles duplicate country codes (e.g. "5555...") and missing DDDs.
 * @param raw - Raw phone string (may contain non-digits, country code, etc.)
 * @returns The sanitized 13-digit string (55 + 11 digits) or null if invalid.
 */
function sanitizeBrazilianPhone(raw: string): string | null {
  let digits = raw.replace(/\D/g, '');

  // Strip duplicate country code: "5555..." → "55..."
  if (digits.startsWith('5555') && digits.length > 13) {
    digits = digits.slice(2);
  }

  // Only accept mobile numbers (11 digits with 9 prefix) — used in WhatsApp/auth flows
  const isMobile = (d: string) =>
    d.length === 11 && isValidBrazilianPhoneNumber(d);

  // Try treating the full digits as a local number first (handles DDD 55 ambiguity)
  // e.g. "55989013076" could be DDD 55 + 989013076, not country code 55 + 98901307
  if (isMobile(digits)) {
    return `55${digits}`;
  }

  // Then try stripping country code
  if (digits.startsWith('55')) {
    const withoutCountryCode = digits.slice(2);
    if (isMobile(withoutCountryCode)) {
      return `55${withoutCountryCode}`;
    }
  }

  return null;
}

export {
  formatPhoneNumber,
  formatPhoneNumberForWhatsApp,
  isValidBrazilianPhoneNumber,
  sanitizeBrazilianPhone,
};
